import { elements, type Element } from "@/data/elements";

/**
 * Electron Config Sprint - pure scoring + answer-normalization logic.
 *
 * The player types the ground-state electron configuration for a given
 * element. We accept several common notations:
 *   - Unicode superscripts ("1s² 2s² 2p⁶") or ASCII digits ("1s2 2s2 2p6").
 *   - Optional caret form ("1s^2") and ** ("1s**2") for typists.
 *   - Noble-gas shorthand ("[Ne] 3s² 3p¹") or fully expanded form.
 *   - Flexible whitespace and case (orbital letters lowercased; bracket
 *     symbol case-folded then re-cased to canonical).
 */

export const ECS_TURN_SECONDS = 25;
export const ECS_FAST_THRESHOLD_S = 8;
export const ECS_POINTS_FAST = 15;
export const ECS_POINTS_SLOW = 8;

export type EcsOutcome = "fast" | "slow" | "wrong" | "timeout";

export interface EcsQuestion {
  /** Atomic number as string - stable id for de-dupe. */
  id: string;
  target: Element;
}

/** Difficulty bands used by the page to gate which elements appear. */
export type EcsDifficulty = "easy" | "medium" | "hard";

/** Easy: Z 1-20 (no d/f). Medium: Z 1-36 (adds 3d, 4s, 4p). Hard: all 118. */
export function poolFor(difficulty: EcsDifficulty): Element[] {
  if (difficulty === "easy") return elements.filter((e) => e.number <= 20);
  if (difficulty === "medium") return elements.filter((e) => e.number <= 36);
  return elements;
}

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

export function generateQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
  difficulty: EcsDifficulty = "medium",
): EcsQuestion {
  const pool = poolFor(difficulty);
  const available = pool.filter((e) => !excludeIds.has(String(e.number)));
  const source = available.length > 0 ? available : pool;
  const target = pickRandom(rng, source);
  return { id: String(target.number), target };
}

// ---------------- normalization ----------------

const SUPER_TO_DIGIT: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
};

/** Convert "1s²" -> "1s2", "[Ne] 3s² 3p¹" -> "[Ne] 3s2 3p1". */
function superscriptsToDigits(s: string): string {
  return s.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (c) => SUPER_TO_DIGIT[c] ?? c);
}

/**
 * Tokenize a configuration into an ordered list of normalized parts.
 * Returns null when input is unparseable.
 *
 * Output token forms:
 *   - Bracket core: "[NE]" (uppercased symbol)
 *   - Orbital:     { n: 2, l: "s", e: 2 } -> serialized as "2S2"
 */
type Token =
  | { kind: "core"; symbol: string }
  | { kind: "orb"; n: number; l: "s" | "p" | "d" | "f"; e: number };

function tokenize(input: string): Token[] | null {
  if (!input) return null;
  // Collapse caret/star markers so "1s^2" and "1s**2" both work.
  let s = superscriptsToDigits(input)
    .replace(/\*\*/g, "")
    .replace(/\^/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return null;

  const tokens: Token[] = [];
  // Bracket noble core, only valid as the very first token.
  const coreMatch = s.match(/^\[\s*([A-Za-z]{1,2})\s*\]\s*/);
  if (coreMatch) {
    tokens.push({ kind: "core", symbol: coreMatch[1].toUpperCase() });
    s = s.slice(coreMatch[0].length);
  }

  // Remaining: sequence of orbital terms like "3s2", "4p10", separated by
  // whitespace, dots, or nothing.
  while (s.length > 0) {
    s = s.replace(/^[\s.,;]+/, "");
    if (s.length === 0) break;
    const m = s.match(/^([1-9])([spdfSPDF])([0-9]{1,2})/);
    if (!m) return null;
    const n = Number(m[1]);
    const l = m[2].toLowerCase() as "s" | "p" | "d" | "f";
    const max = { s: 2, p: 6, d: 10, f: 14 }[l];
    let e = Number(m[3]);
    let consumed = m[0].length;
    // No-space longhand like "1s22s2" greedily reads e=22; back off to a
    // single-digit count when the greedy value exceeds subshell capacity.
    if (e > max && m[3].length === 2) {
      e = Number(m[3][0]);
      consumed -= 1;
    }
    if (e < 1 || e > max) return null;
    tokens.push({ kind: "orb", n, l, e });
    s = s.slice(consumed);
  }

  return tokens.length > 0 ? tokens : null;
}

function serialize(tokens: Token[]): string {
  return tokens
    .map((t) =>
      t.kind === "core" ? `[${t.symbol}]` : `${t.n}${t.l.toUpperCase()}${t.e}`,
    )
    .join(" ");
}

/** Expand a noble-gas core token into its full orbital list (recursive). */
function expandCore(symbol: string): Token[] | null {
  const noble = elements.find(
    (e) => e.symbol.toUpperCase() === symbol && e.category === "noble-gas",
  );
  if (!noble) return null;
  const inner = tokenize(noble.electronConfig);
  if (!inner) return null;
  const out: Token[] = [];
  for (const t of inner) {
    if (t.kind === "core") {
      const nested = expandCore(t.symbol);
      if (!nested) return null;
      out.push(...nested);
    } else {
      out.push(t);
    }
  }
  return out;
}

/** Fully expand any [Core] tokens into orbital tokens. */
function expand(tokens: Token[]): Token[] | null {
  const out: Token[] = [];
  for (const t of tokens) {
    if (t.kind === "core") {
      const inner = expandCore(t.symbol);
      if (!inner) return null;
      out.push(...inner);
    } else {
      out.push(t);
    }
  }
  return out;
}

/** Canonicalize: expand cores, then sort by (n, l-order). */
const L_ORDER: Record<string, number> = { s: 0, p: 1, d: 2, f: 3 };
function canonical(tokens: Token[]): Token[] | null {
  const expanded = expand(tokens);
  if (!expanded) return null;
  // Merge any duplicate (n,l) entries by summing electrons - not a valid
  // ground-state notation, but it lets us be permissive in marking.
  const map = new Map<string, Token & { kind: "orb" }>();
  for (const t of expanded as (Token & { kind: "orb" })[]) {
    const key = `${t.n}${t.l}`;
    const cur = map.get(key);
    if (cur) cur.e += t.e;
    else map.set(key, { ...t });
  }
  return [...map.values()].sort(
    (a, b) => a.n - b.n || L_ORDER[a.l] - L_ORDER[b.l],
  );
}

/**
 * Compare a player's typed configuration to the element's canonical
 * configuration. Accepts either shorthand or expanded forms; both are
 * canonicalized before comparison.
 */
export function checkConfiguration(
  input: string,
  element: Element,
): { correct: boolean; canonicalAnswer: string } {
  const expectedTokens = tokenize(element.electronConfig);
  const expected = expectedTokens ? canonical(expectedTokens) : null;
  const canonicalAnswer = element.electronConfig;
  if (!expected) return { correct: false, canonicalAnswer };

  const userTokens = tokenize(input);
  if (!userTokens) return { correct: false, canonicalAnswer };
  const user = canonical(userTokens);
  if (!user) return { correct: false, canonicalAnswer };

  if (user.length !== expected.length) return { correct: false, canonicalAnswer };
  for (let i = 0; i < user.length; i++) {
    const a = user[i] as Token & { kind: "orb" };
    const b = expected[i] as Token & { kind: "orb" };
    if (a.n !== b.n || a.l !== b.l || a.e !== b.e) {
      return { correct: false, canonicalAnswer };
    }
  }
  return { correct: true, canonicalAnswer };
}

/** Resolve an answer attempt. `null` input = timeout. */
export function scoreAnswer(
  input: string | null,
  question: EcsQuestion,
  elapsedMs: number,
): { points: number; outcome: EcsOutcome; canonicalAnswer: string } {
  const canonicalAnswer = question.target.electronConfig;
  if (input === null) return { points: 0, outcome: "timeout", canonicalAnswer };
  const { correct } = checkConfiguration(input, question.target);
  if (!correct) return { points: 0, outcome: "wrong", canonicalAnswer };
  if (elapsedMs <= ECS_FAST_THRESHOLD_S * 1000) {
    return { points: ECS_POINTS_FAST, outcome: "fast", canonicalAnswer };
  }
  return { points: ECS_POINTS_SLOW, outcome: "slow", canonicalAnswer };
}

/** Re-export so the page can serialize a tokenized form for display. */
export const __testing__ = { tokenize, canonical, serialize, expand };

// ---------------- breakdown (for review panel) ----------------

export interface OrbitalPart {
  /** Principal quantum number, e.g. 3 for "3p". */
  n: number;
  /** Subshell letter. */
  l: "s" | "p" | "d" | "f";
  /** Electron count in this orbital. */
  e: number;
  /** Max electrons this subshell can hold (2/6/10/14). */
  capacity: number;
  /** Pretty label, e.g. "3p\u2075". */
  label: string;
}

export interface ConfigBreakdown {
  /** Noble-gas core symbol (e.g. "Ne") if shorthand was used; null otherwise. */
  core: string | null;
  /** Atomic number represented by the core (electrons it accounts for). */
  coreElectrons: number;
  /** Orbitals written explicitly after the core, in order. */
  orbitals: OrbitalPart[];
  /** Total electrons (should equal Z for a neutral atom). */
  totalElectrons: number;
  /** Highest principal quantum number among the explicit orbitals - the valence shell. */
  valenceShell: number | null;
}

const SUB_DIGITS = ["\u2070", "\u00b9", "\u00b2", "\u00b3", "\u00b4", "\u2075", "\u2076", "\u2077", "\u2078", "\u2079"];
function toSuper(n: number): string {
  return String(n)
    .split("")
    .map((d) => SUB_DIGITS[Number(d)] ?? d)
    .join("");
}

/**
 * Parse the canonical configuration string for an element into a
 * structured breakdown for the review panel. Returns null if the
 * underlying string can't be tokenized (defensive - shouldn't happen
 * for shipped element data).
 */
export function breakdownConfiguration(element: Element): ConfigBreakdown | null {
  const tokens = tokenize(element.electronConfig);
  if (!tokens) return null;

  let core: string | null = null;
  let coreElectrons = 0;
  const orbitals: OrbitalPart[] = [];

  for (const t of tokens) {
    if (t.kind === "core") {
      core = t.symbol.charAt(0) + t.symbol.slice(1).toLowerCase();
      const noble = elements.find(
        (e) => e.symbol.toUpperCase() === t.symbol && e.category === "noble-gas",
      );
      coreElectrons = noble?.number ?? 0;
    } else {
      const capacity = { s: 2, p: 6, d: 10, f: 14 }[t.l];
      orbitals.push({
        n: t.n,
        l: t.l,
        e: t.e,
        capacity,
        label: `${t.n}${t.l}${toSuper(t.e)}`,
      });
    }
  }

  const orbElectrons = orbitals.reduce((sum, o) => sum + o.e, 0);
  const valenceShell =
    orbitals.length > 0 ? Math.max(...orbitals.map((o) => o.n)) : null;

  return {
    core,
    coreElectrons,
    orbitals,
    totalElectrons: coreElectrons + orbElectrons,
    valenceShell,
  };
}